/* eslint camelcase: 0 */
import { ComponentPropsWithRef, type FC } from "react";

import {
  CheckCircleFilledSVG,
  CloseCircleFilledSVG,
  Icon,
  LockFilledSVG,
} from "@newsela/angelou";

import {
  IChecksForUnderstanding,
  ICheckForUnderstandingQuizAnswerOption,
} from "@/types/models/CheckForUnderstandingQuiz.interface";
import AITopicsNotice from "@/components/AIComponents/AIContentNotice/AIContentNotice";
import {
  aiTopicsNoticeWrapperCss,
  collapseButtonTriggerCss,
  eyebrowCss,
  headerCss,
  iconCss,
  lockMessageCss,
  textContainerCss,
} from "./ChecksForUnderstandingHeader.style";

interface ChecksForUnderstandingHeaderProps {
  currentFontSize: string;
  data: IChecksForUnderstanding;
  isActiveQuizQuestion: boolean;
  isCfuBodyHidden?: boolean;
  isStudentPreviewOn: boolean;
  isTeacher: boolean;
  showLunaAiBranding: boolean;
  submittedAnswers: ICheckForUnderstandingQuizAnswerOption[];
}

export type QuizSubmissionStatus = "locked" | "correct" | "incorrect";

export const HeaderContent = (
  submittedAnswers: ICheckForUnderstandingQuizAnswerOption[],
  isTeacher: boolean,
  isStudentPreviewOn: boolean,
): {
  status: QuizSubmissionStatus;
  text: string;
  icon: FC<ComponentPropsWithRef<"svg">>;
} => {
  if (isTeacher && !isStudentPreviewOn) {
    return {
      status: "locked",
      text: "Students must answer the question correctly to continue reading",
      icon: LockFilledSVG,
    };
  }
  if (submittedAnswers?.length === 0) {
    return {
      status: "locked",
      text: "Answer the question correctly to continue reading",
      icon: LockFilledSVG,
    };
  }

  const hasSubmittedCorrectAnswer = submittedAnswers.find(
    (answer: ICheckForUnderstandingQuizAnswerOption) => answer.is_correct,
  );

  return hasSubmittedCorrectAnswer
    ? {
        status: "correct",
        text: "Correct! Continue reading this article",
        icon: CheckCircleFilledSVG,
      }
    : {
        status: "incorrect",
        text: "That's not right, try again.",
        icon: CloseCircleFilledSVG,
      };
};

export const ChecksForUnderstandingHeader = ({
  currentFontSize,
  data,
  isActiveQuizQuestion,
  isCfuBodyHidden,
  isStudentPreviewOn,
  isTeacher,
  showLunaAiBranding,
  submittedAnswers,
}: ChecksForUnderstandingHeaderProps) => {
  const { questionData } = data;
  if (!questionData) return null;
  const { question_number, total_number_of_questions, id } = questionData;
  const countText = `Check For Understanding ${question_number} of ${total_number_of_questions}`;

  const { text, status, icon } = HeaderContent(
    submittedAnswers,
    isTeacher,
    isStudentPreviewOn,
  );

  const isCollapsible =
    (!isTeacher || isStudentPreviewOn) && status === "correct";
  return (
    <div css={headerCss(status, showLunaAiBranding)}>
      <div>
        <div css={eyebrowCss(currentFontSize, status, showLunaAiBranding)}>
          {countText}
        </div>
        <div css={lockMessageCss(status)}>
          <Icon
            SvgComponent={icon}
            size={Icon.sizes.md}
            isDecorative={false}
            title={status}
            __cssFor={{ root: iconCss(status) }}
          />
          <div css={textContainerCss(currentFontSize)}>
            <span aria-live={isActiveQuizQuestion ? "assertive" : "off"}>
              {text}
            </span>
            {isCollapsible && (
              <span css={collapseButtonTriggerCss(currentFontSize)}>
                {isCfuBodyHidden ? "Show" : "Hide"}
              </span>
            )}
          </div>
        </div>
      </div>
      {isTeacher && !isStudentPreviewOn ? (
        <div css={aiTopicsNoticeWrapperCss}>
          <AITopicsNotice
            contentId={id}
            type="ChecksForUnderstanding"
            showLunaText
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};
